/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component — GDPR/privacy consent step of the multi-step booking form
 *   extends:   Component (@glimmer/component)
 *   template:  form-step-modulo.hbs
 *   args:      @step, @applicationController, @onNext
 *   uses:      session/notify/intl services, beebee-base/lib/utility (beebeeAjaxPost, notifyUserError), beebee-base/lib/get-config
 *   siblings:  form-step-dati.js, form-step-project.js, form-progress.js
 *   tests:     form-components-test.js, wave8-coverage-test.js
 *   gotchas:   acceptTask posts each term sequentially via beebeeAjaxPost; a failed
 *              post notifies and returns without calling onNext, leaving later
 *              terms un-posted.
 *
 * Renders the GDPR consent form for the booking's current step, builds the
 * privacy PDF URL, and posts accepted terms via acceptTask before calling
 * @onNext.
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as iService } from '@ember/service';
import { task } from 'ember-concurrency';
import { beebeeAjaxPost, notifyUserError } from 'beebee-base/lib/utility';
import getConfig from 'beebee-base/lib/get-config';

export default class FormStepModuloComponent extends Component {
  @iService session;
  @iService notify;
  @iService intl;

  @tracked _activeStates = {};

  get contactId() {
    return this.session.me?.get('id');
  }

  get moduloIndex() {
    return this.args.step?.id;
  }

  get consenso() {
    const privacyList = this.args.applicationController?.gdprField?.privacy?.data;
    if (!Array.isArray(privacyList)) return null;
    return privacyList.find((c) => String(c.id) === String(this.moduloIndex)) ?? null;
  }

  get pdfUrl() {
    const { urlApi } = getConfig(this).APP;
    const consenso = this.consenso;
    if (consenso?.pdf_url) {
      return `${urlApi}/privacy_new?contact_id=${this.contactId}&access_token=${this.session.token}&index=${this.moduloIndex}&pdf_url=${encodeURIComponent(consenso.pdf_url)}`;
    }
    return `${urlApi}/privacy?contact_id=${this.contactId}&access_token=${this.session.token}&index=${this.moduloIndex}&pdf_url=null`;
  }

  get terms() {
    const termDescriptions = this.consenso?.term_descriptions;
    if (!Array.isArray(termDescriptions) || termDescriptions.length === 0) return [];

    const allTermOptions = this.args.applicationController?.gdprField?.organization_term_options?.data;
    if (!Array.isArray(allTermOptions)) return [];

    const activeStates = this._activeStates;

    return termDescriptions
      .map((name) => {
        const opt = allTermOptions.find((t) => t.name === name);
        if (!opt) return null;
        return {
          option_name: opt.name,
          description: opt.description,
          required: !!opt.required,
          active: activeStates[name] ?? false,
        };
      })
      .filter(Boolean);
  }

  get requiredTermsSatisfied() {
    return this.terms.every((t) => !t.required || t.active);
  }

  @action
  toggleTerm(term, value) {
    this._activeStates = { ...this._activeStates, [term.option_name]: value };
  }

  @(task(function* () {
    const contactId = this.contactId;
    const onNext = this.args.onNext;

    for (const term of this.terms) {
      try {
        yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/save_consensi`,
          this.session.token,
          {
            contact_id: contactId,
            project_id: null,
            consenso: {
              option_name: term.option_name,
              accepted: term.active,
              in_contact: true,
            },
            state: term.active,
          }
        );
      } catch (e) {
        notifyUserError(this.notify, e, this.intl);
        return;
      }
    }

    onNext();
  }).drop())
  acceptTask;

  @action
  accept() {
    if (!this.requiredTermsSatisfied) return;
    this.acceptTask.perform();
  }
}
